import React, {useState} from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NiceModal from "@ebay/nice-modal-react";
import {useFormContext, useWatch} from "react-hook-form";
import {Menu, Text} from "@bounce/shared/ui";
import {Button, MenuItem} from "../../ui/components";
import {ReachAccountSearchResult} from "../../apis/requests/accounts/searchAccounts";
import {ReachTemplate, ReachTemplateLanguage} from "../../apis/requests/templates/types";
import {FormValues, formSchema, MESSAGING_CHANNELS} from "./templateFormSchema";
import EmailSpamCheckDialog from "./EmailSpamCheckDialog";
import TestSendDialog, {TestSendLanguageContent} from "./TestSendDialog";

type ExistingTranslation = Pick<
  ReachTemplate,
  "subject" | "body" | "landing_page_body" | "pre_header" | "channel_message_code"
>;

type Props = {
  disabled: boolean;
  templateId: string | undefined;
  previewAccount: ReachAccountSearchResult | null;
  agentEmail: string | null | undefined;
  existingTranslation: ExistingTranslation | undefined;
};

const TESTABLE_FIELDS = (Object.keys(formSchema.shape) as (keyof FormValues)[]).filter((field) => field !== "name");

const TestSendButton = ({disabled, templateId, previewAccount, agentEmail, existingTranslation}: Props) => {
  const {control, trigger, getValues} = useFormContext<FormValues>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasExistingTranslation = !!(existingTranslation?.subject || existingTranslation?.body);

  const close = () => setAnchorEl(null);

  const buildContentByLanguage = (
    values: FormValues
  ): Partial<Record<ReachTemplateLanguage, TestSendLanguageContent>> => {
    const contentByLanguage: Partial<Record<ReachTemplateLanguage, TestSendLanguageContent>> = {
      en: {
        subject: values.subject || null,
        body: values.body,
        landingPageBody: values.landing_page_body || null,
        preHeader: values.pre_header || null,
        channelMessageCode: values.channel_message_code || null,
      },
    };
    if (hasExistingTranslation) {
      contentByLanguage.es = {
        subject: existingTranslation?.subject || null,
        body: existingTranslation?.body ?? "",
        landingPageBody: existingTranslation?.landing_page_body || null,
        preHeader: existingTranslation?.pre_header || null,
        channelMessageCode: existingTranslation?.channel_message_code || null,
      };
    }
    return contentByLanguage;
  };

  const prepare = async (): Promise<FormValues | null> => {
    if (!previewAccount) return null;
    const isValid = await trigger(TESTABLE_FIELDS);
    if (!isValid) return null;
    return getValues();
  };

  const onSendTest = async () => {
    close();
    const values = await prepare();
    if (!values || !previewAccount) return;
    const defaultRecipient = MESSAGING_CHANNELS.has(values.content_channel) ? "" : agentEmail ?? "";
    NiceModal.show(TestSendDialog, {
      accountId: previewAccount.account_id,
      defaultRecipient,
      templateKey: values.template_key,
      contentChannel: values.content_channel,
      emailDomainType: values.email_domain_type,
      contentByLanguage: buildContentByLanguage(values),
    });
  };

  const onEmailSpamCheck = async () => {
    close();
    const values = await prepare();
    if (!values || !previewAccount) return;
    NiceModal.show(EmailSpamCheckDialog, {
      accountId: previewAccount.account_id,
      templateId,
      templateKey: values.template_key,
      contentChannel: values.content_channel,
      emailDomainType: values.email_domain_type,
      contentByLanguage: buildContentByLanguage(values),
    });
  };

  const isEmail = useWatch({control, name: "content_channel"}) === "email";

  return (
    <>
      <Button
        buttonType="outlined-primary"
        size="small"
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
        disabled={disabled || !previewAccount}
        endIcon={<KeyboardArrowDownIcon sx={{width: 18, height: 18}} />}
      >
        Test
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem onClick={onSendTest}>
          <Text size={14}>Send test</Text>
        </MenuItem>
        {isEmail && (
          <MenuItem onClick={onEmailSpamCheck}>
            <Text size={14}>Test for spam</Text>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default TestSendButton;
